/**
 * 計助ダッシュボード用ファイル保存
 * - 一覧・中身のキャッシュ: IndexedDB
 * - 保存先: ユーザーが任意の場所を選択（File System Access API / ダウンロード）
 * - 拡張子: .keisuke
 */

import {
  validateKeisukeFile,
  type KeisukeDashboardSerialized,
  type AnalysisTypeKey,
} from '../../../stores/systems/keisuke/dashboardStore';

const DB_NAME = 'keisuke-files';
const STORE_NAME = 'files';
const DB_VERSION = 1;

export interface KeisukeFileRecord {
  id: string;
  fileName: string;
  /** 展開前に一覧で表示する解析種類 */
  analysisType: AnalysisTypeKey;
  savedAt: string;
  content: KeisukeDashboardSerialized;
}

export interface KeisukeFileListItem {
  id: string;
  fileName: string;
  analysisType: AnalysisTypeKey;
  savedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function listKeisukeFiles(): Promise<KeisukeFileListItem[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = (req.result as KeisukeFileRecord[]).map((r) => ({
        id: r.id,
        fileName: r.fileName,
        analysisType: r.analysisType,
        savedAt: r.savedAt,
      }));
      rows.sort((a, b) => (b.savedAt > a.savedAt ? 1 : -1));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function getKeisukeFile(id: string): Promise<KeisukeFileRecord | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function putKeisukeFile(record: KeisukeFileRecord): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteKeisukeFile(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

function generateId(): string {
  return crypto.randomUUID?.() ?? `keisuke-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const KEISUKE_EXT = '.keisuke';
const MIME_JSON = 'application/json';

/** ファイル名が .keisuke で終わっていなければ付与 */
export function ensureKeisukeExtension(fileName: string): string {
  return fileName.toLowerCase().endsWith(KEISUKE_EXT) ? fileName : `${fileName}${KEISUKE_EXT}`;
}

/**
 * ユーザーが選択したファイルを開き、検証して IndexedDB に追加して内容を返す。
 * File System Access API 非対応時は従来の file input にフォールバックするため、呼び出し元で file input も用意すること。
 */
export async function openKeisukeFromDisk(): Promise<{
  id: string;
  fileName: string;
  data: KeisukeDashboardSerialized;
  handle?: FileSystemFileHandle;
} | null> {
  if (typeof window === 'undefined' || !('showOpenFilePicker' in window)) {
    return null;
  }
  try {
    const [handle] = await (window as Window & { showOpenFilePicker: (o?: unknown) => Promise<FileSystemFileHandle[]> })
      .showOpenFilePicker({
        types: [{ description: 'Keisuke file', accept: { [MIME_JSON]: [KEISUKE_EXT] } }],
        multiple: false,
      });
    const file = await handle.getFile();
    const text = await file.text();
    const raw = JSON.parse(text);
    const result = validateKeisukeFile(raw);
    if (!result.ok) {
      const err = result as { ok: false; errorKey: string };
      throw new Error(err.errorKey);
    }
    const data = result.data;
    const id = generateId();
    const fileName = file.name;
    const analysisType = (data.analysisOptions?.type as AnalysisTypeKey) ?? 't-test';
    const record: KeisukeFileRecord = {
      id,
      fileName,
      analysisType,
      savedAt: data.savedAt ?? new Date().toISOString(),
      content: data,
    };
    await putKeisukeFile(record);
    return { id, fileName, data, handle };
  } catch (e) {
    if ((e as Error).name === 'AbortError') return null;
    throw e;
  }
}

/**
 * ユーザーが選択した場所に .keisuke で保存し、IndexedDB に同じ内容を put する。
 * 返り値の handle を保持すれば、次回「上書き保存」で同じファイルに書き込める。
 */
export async function saveKeisukeToDisk(
  content: KeisukeDashboardSerialized,
  suggestedFileName: string,
  existingHandle?: FileSystemFileHandle | null
): Promise<{ fileName: string; handle?: FileSystemFileHandle } | null> {
  const fileName = ensureKeisukeExtension(suggestedFileName);
  const json = JSON.stringify(content, null, 2);
  const blob = new Blob([json], { type: MIME_JSON });

  if (existingHandle && typeof (existingHandle as FileSystemFileHandle).createWritable === 'function') {
    try {
      const w = await (existingHandle as FileSystemFileHandle).createWritable();
      await w.write(blob);
      await w.close();
      return { fileName: existingHandle.name, handle: existingHandle };
    } catch {
      // 権限エラー等の場合は Save As にフォールバック
    }
  }

  if (typeof window === 'undefined' || !('showSaveFilePicker' in window)) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return { fileName };
  }
  try {
    const handle = await (window as Window & { showSaveFilePicker: (o?: unknown) => Promise<FileSystemFileHandle> })
      .showSaveFilePicker({
        suggestedName: fileName,
        types: [{ description: 'Keisuke file', accept: { [MIME_JSON]: [KEISUKE_EXT] } }],
      });
    const w = await handle.createWritable();
    await w.write(blob);
    await w.close();
    return { fileName: handle.name, handle };
  } catch (e) {
    if ((e as Error).name === 'AbortError') return null;
    throw e;
  }
}

/** IndexedDB のレコードを更新（名前変更） */
export async function renameKeisukeFile(id: string, newFileName: string): Promise<void> {
  const record = await getKeisukeFile(id);
  if (!record) return;
  const fileName = ensureKeisukeExtension(newFileName);
  await putKeisukeFile({ ...record, fileName, savedAt: new Date().toISOString() });
}

/** フォルダピッカーで選択したディレクトリのハンドルを返す。未対応またはキャンセル時は null */
export async function openFolderFromDisk(): Promise<FileSystemDirectoryHandle | null> {
  if (typeof window === 'undefined' || !('showDirectoryPicker' in window)) {
    return null;
  }
  try {
    const handle = await (window as Window & { showDirectoryPicker: (o?: unknown) => Promise<FileSystemDirectoryHandle> })
      .showDirectoryPicker({ mode: 'read' });
    return handle;
  } catch (e) {
    if ((e as Error).name === 'AbortError') return null;
    throw e;
  }
}

/** ディレクトリ直下のエントリ一覧（名前・種類・ハンドル）。サブディレクトリは再帰しない */
export type DirEntry =
  | { name: string; kind: 'file'; handle: FileSystemFileHandle }
  | { name: string; kind: 'directory'; handle: FileSystemDirectoryHandle };

/** File System Access API の entries() 用（型定義が不足している環境向け） */
type DirHandleWithEntries = FileSystemDirectoryHandle & {
  entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
};

export async function listDirectoryEntries(handle: FileSystemDirectoryHandle): Promise<DirEntry[]> {
  const entries: DirEntry[] = [];
  for await (const [name, entry] of (handle as DirHandleWithEntries).entries()) {
    if (entry.kind === 'file') {
      entries.push({ name, kind: 'file', handle: entry as FileSystemFileHandle });
    } else {
      entries.push({ name, kind: 'directory', handle: entry as FileSystemDirectoryHandle });
    }
  }
  entries.sort((a, b) => {
    const aDir = a.kind === 'directory' ? 1 : 0;
    const bDir = b.kind === 'directory' ? 1 : 0;
    if (bDir !== aDir) return bDir - aDir;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
  return entries;
}

/** ファイルハンドルから .keisuke を読み取り検証してダッシュボード用データを返す */
export async function readKeisukeFromFileHandle(
  handle: FileSystemFileHandle
): Promise<{ fileName: string; data: KeisukeDashboardSerialized } | null> {
  const file = await handle.getFile();
  const text = await file.text();
  const raw = JSON.parse(text);
  const result = validateKeisukeFile(raw);
  if (!result.ok) return null;
  return { fileName: file.name, data: result.data };
}
